import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';

const apiFetch = async (url: string, opts?: RequestInit) => {
  const res = await fetch(url, opts);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
};

export function ReferralPublicPage() {
  const { code } = useParams<{ code: string }>();
  const [form, setForm] = useState({ naam: '', email: '', telefoon: '', interesse: '' });
  const [verstuurd, setVerstuurd] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['referral-public', code],
    queryFn: () => apiFetch(`/api/marketing/public/referral/${code}`),
    enabled: !!code,
  });

  const { mutate, isPending, isError: submitError } = useMutation({
    mutationFn: (body: typeof form) =>
      apiFetch(`/api/marketing/public/referral/${code}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }),
    onSuccess: () => setVerstuurd(true),
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-brand border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow p-8 max-w-md w-full text-center">
          <div className="text-4xl mb-4">❌</div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Ongeldige referral code</h2>
          <p className="text-gray-500">Deze referral link is verlopen of ongeldig.</p>
        </div>
      </div>
    );
  }

  if (verstuurd) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow p-8 max-w-md w-full text-center">
          <div className="text-5xl mb-4">🎉</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-3">Bedankt voor uw registratie!</h2>
          <p className="text-gray-600 mb-4">
            Ons team neemt binnenkort contact met u op om een afspraak in te plannen.
          </p>
          {data.programma?.beloning && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
              <strong>Uw voordeel:</strong> {data.programma.beloning}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-brand rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-white text-2xl">🤝</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-800">U bent uitgenodigd!</h1>
          {data.verwijzerNaam && (
            <p className="text-gray-600 mt-2">
              <strong>{data.verwijzerNaam}</strong> heeft u doorverwezen naar IXINA Keukens.
            </p>
          )}
        </div>

        {/* Beloning */}
        {data.programma?.beloning && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <p className="text-sm font-medium text-amber-800">🎁 Uw voordeel als u een keuken bestelt:</p>
            <p className="text-amber-900 font-semibold mt-1">{data.programma.beloning}</p>
          </div>
        )}

        {/* Form */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            mutate(form);
          }}
          className="space-y-4"
        >
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Uw naam *</label>
            <input
              type="text"
              required
              value={form.naam}
              onChange={(e) => setForm((f) => ({ ...f, naam: e.target.value }))}
              placeholder="Jan De Vos"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">E-mailadres *</label>
            <input
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              placeholder="jan@voorbeeld.be"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Telefoonnummer</label>
            <input
              type="tel"
              value={form.telefoon}
              onChange={(e) => setForm((f) => ({ ...f, telefoon: e.target.value }))}
              placeholder="+32 470 00 00 00"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Uw interesse</label>
            <select
              value={form.interesse}
              onChange={(e) => setForm((f) => ({ ...f, interesse: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
            >
              <option value="">Selecteer...</option>
              <option value="nieuwe keuken">Nieuwe keuken</option>
              <option value="renovatie">Renovatie bestaande keuken</option>
              <option value="advies">Vrijblijvend advies</option>
              <option value="andere">Andere</option>
            </select>
          </div>

          {submitError && (
            <p className="text-sm text-red-600">Er ging iets mis. Probeer opnieuw.</p>
          )}

          <button
            type="submit"
            disabled={isPending}
            className="w-full bg-brand hover:bg-[#b8993f] text-white font-semibold py-2.5 rounded-lg transition-colors disabled:opacity-60"
          >
            {isPending ? 'Bezig...' : 'Aanvraag indienen'}
          </button>
        </form>

        {/* Voorwaarden */}
        {data.programma?.voorwaarden && (
          <p className="text-xs text-gray-400 mt-4 text-center">{data.programma.voorwaarden}</p>
        )}
      </div>
    </div>
  );
}
